import React, { useState, useEffect, useCallback } from 'react'
import { Link } from 'react-router-dom'
import contentService from '../services/contentService'
import Loading from '../components/Loading'
// Labels de estado y definición de "vencido" compartidas con el modal del Calendario.
import { getStatusLabel, diasVencido, fetchVencidos } from '../utils/vencidos'

function EmptyState({ icon, title, desc }) {
  return (
    <div className="g-empty">
      <i className={`bi ${icon} g-empty-icon`}></i>
      <p className="g-empty-title">{title}</p>
      {desc && <p className="g-empty-desc">{desc}</p>}
    </div>
  )
}

function Toast({ message, type, onClose }) {
  useEffect(() => {
    const t = setTimeout(onClose, 5000)
    return () => clearTimeout(t)
  }, [onClose])
  return (
    <div style={{
      position: 'fixed', top: 20, right: 20, zIndex: 9999,
      display: 'flex', alignItems: 'center', gap: 10,
      padding: '12px 18px', borderRadius: 'var(--g-radius-md)',
      background: type === 'success' ? '#f0fdf4' : '#fef2f2',
      border: `1px solid ${type === 'success' ? '#86efac' : '#fca5a5'}`,
      color: type === 'success' ? '#166534' : '#991b1b',
      boxShadow: 'var(--g-shadow-md)',
      fontSize: 'var(--g-font-size-sm)', fontWeight: 600,
      maxWidth: 360,
    }}>
      <i className={`bi ${type === 'success' ? 'bi-check-circle-fill' : 'bi-exclamation-circle-fill'}`}
         style={{ fontSize: 16, flexShrink: 0 }}></i>
      <span style={{ flex: 1 }}>{message}</span>
      <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', opacity: 0.6, padding: 0, marginLeft: 4 }}>
        <i className="bi bi-x" style={{ fontSize: 18 }}></i>
      </button>
    </div>
  )
}

export default function VencidosPage() {
  const [posts, setPosts]     = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError]     = useState(null)

  const [selectMode, setSelectMode] = useState(false)
  const [selectedIds, setSelectedIds] = useState(new Set())
  const [aplicando, setAplicando] = useState(false)

  const [toast, setToast] = useState(null)
  const showToast = (message, type = 'success') => setToast({ message, type })

  const cargarVencidos = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      // Misma definición de "vencido" que usa el modal del Calendario (utils/vencidos.js).
      const vencidos = await fetchVencidos()
      setPosts(vencidos)
      setSelectedIds(new Set())
    } catch (e) {
      setError('No se pudieron cargar los posts vencidos.')
      setPosts([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { cargarVencidos() }, [cargarVencidos])

  const toggleSelectMode = () => {
    setSelectMode(prev => !prev)
    setSelectedIds(new Set())
  }

  const toggleSelected = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const toggleSelectAll = () => {
    setSelectedIds(prev => (
      prev.size === posts.length ? new Set() : new Set(posts.map(p => p.id))
    ))
  }

  const aplicarEstado = async (status, label) => {
    if (selectedIds.size === 0) return
    setAplicando(true)
    try {
      const ids = Array.from(selectedIds)
      const resultados = await Promise.allSettled(
        ids.map(id => contentService.updatePost(id, { status }))
      )
      const fallidos = resultados.filter(r => r.status === 'rejected').length
      const exitosos = ids.length - fallidos

      if (exitosos > 0) {
        // Tanto "publicados" como "no publicados" salen de la lista de vencidos:
        // published porque ya no está pendiente, no_publicado porque no debe seguir contando como vencido.
        setPosts(prev => prev.filter(p => !selectedIds.has(p.id)))
      }

      if (fallidos === 0) {
        showToast(`${exitosos} post${exitosos === 1 ? '' : 's'} marcado${exitosos === 1 ? '' : 's'} como ${label}`)
      } else {
        showToast(`${exitosos} actualizados, ${fallidos} con error`, 'error')
      }

      setSelectedIds(new Set())
      setSelectMode(false)
    } catch (e) {
      showToast('Error al actualizar los posts seleccionados', 'error')
    } finally {
      setAplicando(false)
    }
  }

  return (
    <div className="g-page">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      <div className="g-page-header">
        <div>
          <h1 className="g-page-title">
            <i className="bi bi-exclamation-triangle-fill" style={{ marginRight: 10, color: '#dc2626' }}></i>
            Vencidos
          </h1>
          <p className="g-page-subtitle">
            Posts que ya pasaron su fecha de publicación y aún no están marcados como publicados.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {selectMode && (
            <button onClick={toggleSelectMode} className="g-btn g-btn-secondary" disabled={aplicando}>
              Cancelar
            </button>
          )}
          {!selectMode && (
            <button
              onClick={toggleSelectMode}
              className="g-btn g-btn-primary"
              disabled={loading || posts.length === 0}
            >
              <i className="bi bi-check2-square"></i>
              Publicar
            </button>
          )}
          <button onClick={cargarVencidos} className="g-btn g-btn-secondary" disabled={loading || aplicando}>
            <i className={`bi bi-arrow-clockwise ${loading ? 'spin' : ''}`}></i>
            Actualizar
          </button>
        </div>
      </div>

      {selectMode && (
        <div className="g-card" style={{ marginBottom: 16 }}>
          <div className="g-card-body" style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 'var(--g-font-size-sm)', fontWeight: 600, color: 'var(--g-text-sec)' }}>
              {selectedIds.size} seleccionado{selectedIds.size === 1 ? '' : 's'}
            </span>
            <button
              onClick={() => aplicarEstado('published', 'publicados')}
              className="g-btn g-btn-primary"
              disabled={selectedIds.size === 0 || aplicando}
            >
              {aplicando ? <span className="g-spinner" style={{ width: 12, height: 12, borderWidth: 2 }}></span> : <i className="bi bi-check2-circle"></i>}
              Marcar como publicados
            </button>
            <button
              onClick={() => aplicarEstado('no_publicado', 'no publicados')}
              className="g-btn g-btn-secondary"
              disabled={selectedIds.size === 0 || aplicando}
            >
              <i className="bi bi-x-circle"></i>
              Marcar como no publicados
            </button>
          </div>
        </div>
      )}

      <div className="g-card">
        <div className="g-card-header">
          <span className="g-card-header-title">
            {loading ? 'Cargando…' : `${posts.length} post${posts.length === 1 ? '' : 's'} vencido${posts.length === 1 ? '' : 's'}`}
          </span>
        </div>
        <div className="g-card-body" style={{ padding: 0 }}>
          {loading ? (
            <Loading fullScreen={false} mensaje="Cargando" />
          ) : error ? (
            <EmptyState icon="bi-exclamation-circle" title={error} />
          ) : posts.length === 0 ? (
            <EmptyState
              icon="bi-check2-circle"
              title="No hay posts vencidos"
              desc="Todos los posts programados hasta hoy están al día."
            />
          ) : (
            <div className="g-table-wrap">
              <table className="g-table">
                <thead>
                  <tr>
                    {selectMode && (
                      <th style={{ width: 36 }}>
                        <input
                          type="checkbox"
                          checked={selectedIds.size === posts.length}
                          onChange={toggleSelectAll}
                        />
                      </th>
                    )}
                    <th>Cuenta</th>
                    <th>Post</th>
                    <th>Plataforma</th>
                    <th>Estado</th>
                    <th>Fecha programada</th>
                    <th>Días vencido</th>
                  </tr>
                </thead>
                <tbody>
                  {posts.map(post => (
                    <tr key={post.id}>
                      {selectMode && (
                        <td>
                          <input
                            type="checkbox"
                            checked={selectedIds.has(post.id)}
                            onChange={() => toggleSelected(post.id)}
                          />
                        </td>
                      )}
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          {post.influencer_profile_image_url ? (
                            <img src={post.influencer_profile_image_url} alt=""
                              style={{ width: 24, height: 24, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
                              onError={e => { e.target.style.display = 'none' }} />
                          ) : null}
                          <span>{post.influencer_name || post.influencer || '—'}</span>
                        </div>
                      </td>
                      <td style={{ maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {post.title || '(sin título)'}
                      </td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          {post.platform_icon_url ? (
                            <img src={post.platform_icon_url} alt={post.platform_name || ''} title={post.platform_name || ''} style={{ width: 18, height: 18 }} />
                          ) : (post.platform_name || '—')}
                        </div>
                      </td>
                      <td>
                        <span className="g-badge g-badge-danger">
                          {getStatusLabel(post.status)}
                        </span>
                      </td>
                      <td>
                        {new Date(post.publication_date.slice(0, 10) + 'T00:00:00').toLocaleDateString('es-CL', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </td>
                      <td>
                        <span className="g-badge g-badge-warning">
                          {diasVencido(post.publication_date)} día{diasVencido(post.publication_date) === 1 ? '' : 's'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <p style={{ marginTop: 12, fontSize: 'var(--g-font-size-xs)', color: 'var(--g-text-muted)' }}>
        <i className="bi bi-info-circle" style={{ marginRight: 4 }}></i>
        Para editar un post vencido en detalle, hazlo desde el <Link to="/">Calendario</Link>.
      </p>

      <style>{`
        .spin { animation: spin 0.8s linear infinite; }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .g-spinner {
          width: 28px; height: 28px;
          border: 3px solid var(--g-border);
          border-top-color: var(--g-lima);
          border-radius: 50%;
          animation: spin 0.7s linear infinite;
          display: inline-block;
        }
      `}</style>
    </div>
  )
}
